import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Stack, Typography, Select, MenuItem } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { LTCPContext } from "LTCP Module/LTCPContext";
import { getPipelineRunStatus, getDataCheckFailedFiles } from "LTCP Module/api/ltcpBackend";

export default function StatusCard({ userEmail, runType }) {
  const {
    setLoading,
    pipelineManualProductType,
    pipelineManualTimeStamp,
    pipelineScheduleTimeStamp,
    pipelineManualProductTypeOptions,
    pipelineManualTimeStampOptions,
    pipelineScheduleTimeStampOptions,
    changePipelineManualProductType,
    changePipelineManualTimeStamp,
    changePipelineScheduleTimeStamp,
  } = useContext(LTCPContext);

  const [demandStatus, setDemandStatus] = useState({
    v1: "No Run",
    m1: "No Run",
    m2: "No Run",
    sourcingMatrix:'',
    capacityMaster:'',
    user: "",
  });

  async function fetchStatus() {
    try {
      setLoading(true);

      let resp;

      if (runType === "Manual") {
        resp = await getPipelineRunStatus(
          pipelineManualTimeStamp,
          "Manual",
          userEmail,
          pipelineManualProductType
        );
      } else {
        resp = await getPipelineRunStatus(
          pipelineScheduleTimeStamp,
          "Schedule",
          userEmail,
          "All"
        );
      }
      setDemandStatus(resp);

      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.error(e);
    }
  }

  async function DataCheckStatus(type, userEmail) {
    try {
      setLoading(true);
      await getDataCheckFailedFiles({ type, user_email: userEmail })

      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.error(e);
    }
  }

  useEffect(() => {
    fetchStatus();
  }, [
    pipelineManualProductType,
    pipelineManualTimeStamp,
    pipelineScheduleTimeStamp,
  ]);
  return (
    <Box
      sx={{
        border: "1px solid #ccc",
        borderRadius: 2,
        p: 2,
        backgroundColor: "#fff",
        boxShadow: 1,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 4 }}>
        <Stack spacing={1}>
          <Typography sx={{ fontSize: 14 }}>
            <strong>Run Type :</strong> {runType}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", fontSize: 14 }}>
            <strong>Product Type :</strong>
            {runType === "Schedule" ? (
              <Select
                size="small"
                value="All"
                disabled
                sx={{
                  ml: 1,
                  px: 2,
                  py: 0,
                  height: 32,
                  width: 180,
                  fontSize: 14,
                  bgcolor: "#fff",
                }}
              >
                <MenuItem value="All">All</MenuItem>
              </Select>
            ) : (
              <Select
                size="small"
                value={pipelineManualProductType}
                onChange={(e) =>
                  changePipelineManualProductType(e.target.value)
                }
                sx={{
                  ml: 1,
                  px: 2,
                  py: 0,
                  height: 32,
                  width: 180,
                  fontSize: 14,
                  bgcolor: "#fff",
                }}
              >
                {pipelineManualProductTypeOptions.map((item, index) => (
                  <MenuItem key={index * 2} value={item}>
                    {item.replaceAll("_", " ")}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", fontSize: 14 }}>
            <strong>Run Timestamp :</strong>
            {runType === "Manual" ? (
              <Select
                size="small"
                value={pipelineManualTimeStamp}
                onChange={(e) => changePipelineManualTimeStamp(e.target.value)}
                sx={{
                  ml: 1,
                  px: 2,
                  py: 0,
                  height: 32,
                  width: 180,
                  fontSize: 14,
                  bgcolor: "#fff",
                }}
              >
                {pipelineManualTimeStampOptions.map((item, index) => (
                  <MenuItem key={index * 2} value={item}>
                    {new Date(item.replaceAll("_", " ")).toLocaleString()}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Select
                size="small"
                value={pipelineScheduleTimeStamp}
                onChange={(e) =>
                  changePipelineScheduleTimeStamp(e.target.value)
                }
                sx={{
                  ml: 1,
                  px: 2,
                  py: 0,
                  height: 32,
                  width: 180,
                  fontSize: 14,
                  bgcolor: "#fff",
                }}
              >
                {pipelineScheduleTimeStampOptions.map((item, index) => (
                  <MenuItem key={index * 2} value={item}>
                    {new Date(item.replaceAll("_", " ")).toLocaleString()}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>

          {runType === "Manual" && (
            <Typography sx={{ fontSize: 14 }}>
              <strong>User ID:</strong> {demandStatus.user}
            </Typography>
          )}
          {runType === "Manual" &&
            demandStatus.sourcingMatrix === "Run Failed- Data Check Error" && (
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ alignSelf: "flex-start" }}
                  onClick={() => DataCheckStatus("sourcing_matrix", userEmail)}
                >
                  Sourcing Matrix Error File
                </Button>
              </Box>
            )}
          {runType === "Manual" &&
            demandStatus.capacityMaster === "Run Failed- Data Check Error" && (
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ alignSelf: "flex-start" }}
                  onClick={() => DataCheckStatus("capacity_master", userEmail)}
                >
                  Capacity Master Error File
                </Button>
              </Box>
            )}
        </Stack>

        <Stack spacing={1}>
          <DemandStatusRow label="V1 Demand" status={demandStatus.v1} />
          <DemandStatusRow label="M1 Demand" status={demandStatus.m1} />
          <DemandStatusRow label="M2 Demand" status={demandStatus.m2} />
        </Stack>
      </Box>
    </Box>
  );
}

// Reusable component for demand status row
function DemandStatusRow({ label, status }) {
  let color = "red";

  if (status === "Completed" || status === "Run Successful") color = "teal";
  else if (status === "In Progress") color = "orange";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography sx={{ fontSize: 14 }}>
        <strong>{label} :</strong>
      </Typography>
      {(status === "Completed" || status === "Run Successful") && (
        <CheckCircleIcon sx={{ fontSize: 14 }} />
      )}

      {status === "In Progress" && <AutorenewIcon sx={{ fontSize: 14 }} />}

      {(status === "No run" ||
        status === "Failed" ||
        status === "Error" ||
        !status) && <WarningAmberIcon sx={{ fontSize: 14 }} />}

      <Typography sx={{ color, fontSize: 14 }}>{status || "No run"}</Typography>
    </Box>
  );
}

StatusCard.propTypes = {
  userEmail: PropTypes.string.isRequired,
  runType: PropTypes.oneOf(["Manual", "Schedule"]).isRequired,
};

DemandStatusRow.propTypes = {
  label: PropTypes.string.isRequired,
  status: PropTypes.string,
};
